import * as React from 'react'
import {ChangeEvent, Component, ReactElement} from 'react'
import {Box} from '@mui/material'
import {NatApplicationRepo} from '../../repository/NatApplicationRepo'
import {BlockSpacer, currentMedia} from '../../../../../styledComponents/ResponsiveConvenience'
import {FlexContainer, FullWidthColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {convertToRoomNumberForResident, getCurrentYear} from '../../../../common/util/Natutil'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import {MONTH} from '../../../../../CodeMaster'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {ApplyButton, ItemHeading, WideWhiteArea} from '../../common/resident/NatResidentApplicationsCommon'
import {NatResidentBicycleParkingUseWithdrawalLocationState} from '../NatResidentApplicationInterface'
import {Nullable} from '../../../../../app/TypeHelpers'
import EventEmitterExResident from '../../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import NatCondominiumRepo from '../../../condominium/repository/NatCondominiumRepo'
import NatResidentApplicationDetailFormItem from '../../common/resident/NatResidentApplicationDetailFormItem'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import {PPTextFieldForResident} from '../../../../../components/shared/forms/resident/PPTextFieldForResident'
import styled from 'styled-components'
import {NatPutBicycleParkingUseWithdrawalApplyRequest} from '../../repository/NatPutBicycleParkingUseWithdrawal'
import ErrorMessageAreaWrapper from '../../../../../components/shared/forms/resident/ErrorMessageAreaWrapper'
import PPSelectForResident, {Option} from '../../../../../components/shared/forms/resident/PPSelectForResident'
import moment from 'moment'
import {NatGetCurrentDateTime} from '../../../currentDateTime/repository/NatGetCurrentDateTime'
import PPDatePickerForResident from '../../../../../components/shared/forms/resident/PPDatePickerForResident'
import {HKConstants} from '../../../../../HKConstants'
import HolidaysRepository from '../../../../../components/HolidaysRepository'
import {StyledDiv} from '../../../../../styledComponents/StyledTypeDefinitions'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    condominiumRepo: NatCondominiumRepo
    holidaysRepo: HolidaysRepository
    navigate: NavigateFunction
    location: Location<NatResidentBicycleParkingUseWithdrawalLocationState>
    eventEmitter: EventEmitterExResident
    asyncCallFunction: AsyncCallFunctionType

    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    withdrawalArea: Nullable<string>
    withdrawalDate: Nullable<string>
    bicycleCarryingOutDate: Nullable<string>

    submitButtonLabel: string
    onClickApplyConfirmButton: (request: NatPutBicycleParkingUseWithdrawalApplyRequest) => Promise<void>
}

interface State {
    applicantFamilyName: string
    applicantFirstName: string
    withdrawalArea: string
    withdrawalDateYear: string
    withdrawalDateMonth: string
    bicycleCarryingOutDate: string
    currentDate: moment.Moment
    holidays: string[]
    yearOptions: Option[]
    monthOptions: Option[]
    clearWithdrawalDateError: boolean
    applyButtonDisabled: boolean
    isLoadCompleted: boolean
}

export default class NatResidentBicycleParkingUseWithdrawalForm extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            applyButtonDisabled: true,
            applicantFamilyName: '',
            applicantFirstName: '',
            withdrawalArea: '',
            withdrawalDateYear: '',
            withdrawalDateMonth: '',
            bicycleCarryingOutDate: '',
            currentDate: moment(),
            holidays: [],
            yearOptions: [],
            monthOptions: [],
            clearWithdrawalDateError: false,
            isLoadCompleted: false
        }
        this.handleChangeText = this.handleChangeText.bind(this)
        this.handleChangeWithdrawalDateYear = this.handleChangeWithdrawalDateYear.bind(this)
        this.handleChangeWithdrawalDateMonth = this.handleChangeWithdrawalDateMonth.bind(this)
        this.handleChangeBicycleCarryingOutDate = this.handleChangeBicycleCarryingOutDate.bind(this)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const [currentTimeResponse, holidays] = await Promise.all([
                this.getCurrentDateTime(),
                this.getHoliday(),
            ])

            // 現在日時取得
            if (currentTimeResponse && currentTimeResponse.details) {
                const currentYear: number = getCurrentYear(`${currentTimeResponse.details.currentDateTime}`)
                const yearOptions: Option[] = [{label: '', value: ''}]
                const monthOptions: Option[] = [{label: '', value: ''}]

                let year: number = currentYear + 3
                for (let index: number = 1; index <= 7; index++) {
                    yearOptions.push({label: `${year}`, value: `${year}`})
                    year--
                }

                MONTH.values().map((month) => {
                    return (
                        monthOptions.push({
                            label: `${month.label}`, value: (String(month.value).length === 1)
                                ? '0' + String(month.value) : String(month.value)
                        })
                    )
                })
                this.setState({
                    currentDate: moment(currentTimeResponse.details.currentDateTime),
                    yearOptions: yearOptions,
                    monthOptions: monthOptions
                })
            }
            const applicantFamilyName = this.props.applicantFamilyName ?? this.state.applicantFamilyName
            const applicantFirstName = this.props.applicantFirstName ?? this.state.applicantFirstName
            const withdrawalArea = this.props.withdrawalArea ?? this.state.withdrawalArea
            const withdrawalDate = this.props.withdrawalDate ? this.props.withdrawalDate.split('-') : []
            const bicycleCarryingOutDate = this.props.bicycleCarryingOutDate ?? this.state.bicycleCarryingOutDate
            this.setState({
                applicantFamilyName: applicantFamilyName,
                applicantFirstName: applicantFirstName,
                withdrawalArea: withdrawalArea,
                withdrawalDateYear: withdrawalDate[0] ?? this.state.withdrawalDateYear,
                withdrawalDateMonth: withdrawalDate[1] ?? this.state.withdrawalDateMonth,
                bicycleCarryingOutDate: bicycleCarryingOutDate,
                holidays: holidays === null ? [] : holidays
            }, () => {
                this.updateButtonDisabled()
            })
        } finally {
            this.setState({isLoadCompleted: true})
            this.props.eventEmitter.closeLoading()
        }
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        return await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    async getHoliday(): Promise<Nullable<string[]>> {
        const {holidaysRepo, eventEmitter} = this.props

        const minDate: string = HKConstants.CMN.SYC.MIN_HOLIDAY_DATE
        const maxDate: string = HKConstants.CMN.SYC.MAX_HOLIDAY_DATE
        return await this.props.asyncCallFunction(
            holidaysRepo.getHolidays.bind(holidaysRepo, minDate, maxDate),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value}, () => {
            this.updateButtonDisabled()
        })
    }

    handleChangeWithdrawalDateYear(event: React.ChangeEvent<HTMLSelectElement>): void {
        this.setState({withdrawalDateYear: event.target.value, clearWithdrawalDateError: true}, () => {
            this.updateButtonDisabled()
        })
    }

    handleChangeWithdrawalDateMonth(event: React.ChangeEvent<HTMLSelectElement>): void {
        this.setState({withdrawalDateMonth: event.target.value, clearWithdrawalDateError: true}, () => {
            this.updateButtonDisabled()
        })
    }

    handleChangeBicycleCarryingOutDate(date: string): void {
        this.setState({bicycleCarryingOutDate: date}, () => {
            this.updateButtonDisabled()
        })
    }

    updateButtonDisabled(): void {
        const {
            applicantFamilyName, applicantFirstName, withdrawalArea,
            withdrawalDateYear, withdrawalDateMonth, bicycleCarryingOutDate
        } = this.state

        let applyButtonDisabled = false

        if (!applicantFamilyName || !applicantFirstName || !withdrawalArea ||
            !withdrawalDateYear || !withdrawalDateMonth || !bicycleCarryingOutDate) {
            applyButtonDisabled = true
        }

        this.setState({applyButtonDisabled: applyButtonDisabled})
    }

    async onClickApplyConfirmButton(): Promise<void> {
        const {
            applicantFamilyName, applicantFirstName, withdrawalArea,
            withdrawalDateYear, withdrawalDateMonth, bicycleCarryingOutDate
        } = this.state

        this.setState({clearWithdrawalDateError: false})

        const date = withdrawalDateYear + '-' + withdrawalDateMonth

        const request: NatPutBicycleParkingUseWithdrawalApplyRequest = {
            applicantFamilyName: applicantFamilyName,
            applicantFirstName: applicantFirstName,
            withdrawalArea: withdrawalArea,
            withdrawalDate: date,
            bicycleCarryingOutDate: bicycleCarryingOutDate,
            confirmed: false
        }

        if (!request) {
            return
        }
        await this.props.onClickApplyConfirmButton(request)
    }

    renderApplicantForm(): ReactElement {
        const {applicantFamilyName, applicantFirstName} = this.state

        const applicantRoom = NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
            NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_RIDGENAME) + ' ' +
            convertToRoomNumberForResident(
                NatResidentLoginInfoSessionStorageHelper
                    .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER)
            )

        return (
            <>
                <ItemHeading>
                    申請者
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {applicantRoom}
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'氏名'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFamilyName',
                                        maxLength: 10,
                                        value: applicantFamilyName,
                                        onChange: this.handleChangeText,
                                        placeholder: '例：素敵'
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFamilyName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                <FlexContainer>
                                    <BlockSpacer width={5}/>
                                </FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFirstName',
                                        maxLength: 10,
                                        value: applicantFirstName,
                                        placeholder: '例：太郎'
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFirstName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderWithdrawalAreaForm(): ReactElement {
        const {withdrawalArea} = this.state

        return (
            <>
                <ItemHeading>
                    解除する区画
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>自転車置場の<BreakPcOnly/>区画番号</span>}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        maxLength: 20,
                                        name: 'withdrawalArea',
                                        value: withdrawalArea
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['withdrawalArea']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderWithdrawalDateForm(): ReactElement {
        const {withdrawalDateYear, withdrawalDateMonth} = this.state
        return (
            <>
                <ItemHeading>
                    解除日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>自転車置場の使用を<BreakPcOnly/>解除する日付</span>}
                        required
                        component={
                            <ErrorMessageAreaWrapper>
                                <DisplayFlex>
                                    <UnitBoxArea>
                                        <PPSelectForResident
                                            inputProps={{
                                                value: withdrawalDateYear,
                                            }}
                                            placeholder={'年'}
                                            onChange={this.handleChangeWithdrawalDateYear}
                                            options={this.state.yearOptions}
                                            eventKeys={[`withdrawalDate`]}
                                            eventEmitter={this.props.eventEmitter}
                                            clearError={this.state.clearWithdrawalDateError}
                                        />
                                    </UnitBoxArea>
                                    <FlexContainer>
                                        <BlockSpacer width={5}/>
                                    </FlexContainer>
                                    <UnitBoxArea>
                                        <PPSelectForResident
                                            inputProps={{
                                                value: withdrawalDateMonth,
                                            }}
                                            placeholder={'月'}
                                            onChange={this.handleChangeWithdrawalDateMonth}
                                            options={this.state.monthOptions}
                                            eventKeys={[`withdrawalDate`]}
                                            eventEmitter={this.props.eventEmitter}
                                            messageInvisible
                                            clearError={this.state.clearWithdrawalDateError}
                                        />
                                    </UnitBoxArea>
                                    <FlexContainer>
                                        <BlockSpacer width={8}/>
                                    </FlexContainer>
                                    <StringFlexDiv>末日</StringFlexDiv>
                                </DisplayFlex>
                            </ErrorMessageAreaWrapper>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderBicycleCarryingOutDateForm(): ReactElement {
        const {bicycleCarryingOutDate, holidays} = this.state
        return (
            <>
                <ItemHeading>
                    自転車搬出日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>自転車置場から<BreakPcOnly/>自転車を搬出する日付</span>}
                        required
                        component={
                            <PPDatePickerForResident
                                date={bicycleCarryingOutDate}
                                onChange={this.handleChangeBicycleCarryingOutDate}
                                holidays={holidays}
                                itemValue={''}
                                eventKeys={['bicycleCarryingOutDate']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {applyButtonDisabled} = this.state

        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                {this.renderApplicantForm()}
                <Box height={'15px'}/>
                {this.renderWithdrawalAreaForm()}
                <Box height={'15px'}/>
                {this.renderWithdrawalDateForm()}
                <Box height={'15px'}/>
                {this.renderBicycleCarryingOutDateForm()}
                <ApplyButton disabled={applyButtonDisabled} onClick={this.onClickApplyConfirmButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
            </>
        )
    }
}

const BreakPcOnly = styled('br')`
  ${currentMedia.lessThan('sp')`
    display: none;
  `};
`

const DisplayFlex: StyledDiv = styled.div`
  display: flex;
`
const UnitBoxArea: StyledDiv = styled.div`
    width: 150px;
  ${currentMedia.greaterThan('pc')`
    width: 165px;
  `};
`
const StringFlexDiv: StyledDiv = styled.div`
  display: flex;
  max-height: 50px;
  align-items: flex-end;
`
